import keycode from 'keycode';
import PropTypes from 'prop-types';
import any from "ramda/es/any";
import append from "ramda/es/append";
import compose from "ramda/es/compose";
import filter from "ramda/es/filter";
import isEmpty from "ramda/es/isEmpty";
import map from "ramda/es/map";
import mergeAll from "ramda/es/mergeAll";
import toString from "ramda/es/toString";
import when from "ramda/es/when";
import cx from "ramda-extension/es/cx";
import defaultToEmptyString from "ramda-extension/es/defaultToEmptyString";
import isNotEmpty from "ramda-extension/es/isNotEmpty";
import isNumber from "ramda-extension/es/isNumber";
import React, { Children, Component } from 'react';
import { createPortal, findDOMNode } from 'react-dom';
import { Popper } from 'react-popper';
import { withDefaultComponentProps } from '../../../decorators/withDefaultComponentProps';
import FormGroup from '../../../molecules/FormGroup';
import { validationDisplayTypePropType } from '../../../molecules/Validation/propTypes';
import { getOffsetWidth } from '../../../utils/dimensions';
import filterChildren from '../../../utils/filterChildren';
import AngleDown from '../../SvgIcon/Icons/AngleDown';
import AngleUp from '../../SvgIcon/Icons/AngleUp';
import SelectFilters from '../SelectFilters';
import SelectInput from '../SelectInput';
import SelectList from '../SelectList';
import SelectPanelControls from '../SelectPanelControls';
import SelectPositionObserver from '../SelectPositionObserver';
const COMPONENT_NAME = 'Select';
const toStringWhenNumber = when(isNumber, toString);
const prop = (pprop)=>(obj)=>obj?.[pprop];
const getProps = prop('props');
const resetFocusState = {
    focusedIndex: -1,
    selectedIndex: -1
};
class Select extends Component {
    constructor(props){
        super(props);
        this._getValueFromOption = compose(prop(props.valueBy), getProps);
        this._getLabelFromOption = compose(prop(props.filterBy), getProps);
        const searchText = props.freetext ? props.value : this.getDefaultSearchText(props);
        this.skipInputBlur = false;
        this.filteredChildren = this.getFilteredChildren(props.children, props.value);
        this.state = {
            target: null,
            targetInput: null,
            open: false,
            searchText,
            focusedIndex: -1,
            selectedIndex: -1
        };
    }
    UNSAFE_componentWillReceiveProps(nextProps) {
        const { children, value, freetext } = nextProps;
        this.filteredChildren = freetext ? this.filterOptions(children, toStringWhenNumber(value)) : this.getFilteredChildren(children, value);
        const freetextSearchText = this.state.open ? toStringWhenNumber(value) : this.getDefaultedFreetextSearchText(value);
        const searchText = freetext ? freetextSearchText : this.getDefaultSearchText(nextProps);
        this.setState((state)=>{
            const updateSearchText = freetext ? state.searchText !== value : !freetext || searchText && this.props.value !== value;
            const toMerge = [
                state,
                resetFocusState
            ];
            return mergeAll(updateSearchText ? append({
                searchText
            }, toMerge) : toMerge);
        });
    }
    getInputNode() {
        return this.control.getInputNode ? this.control.getInputNode() : findDOMNode(this.control);
    }
    blur() {
        const node = this.getInputNode();
        if (node) {
            node.blur();
        }
    }
    focus() {
        const node = this.getInputNode();
        if (node) {
            node.focus();
        }
    }
    open(cb, event) {
        const target = findDOMNode(this);
        const selected = this.getSelected(this.filteredChildren, this.props.value);
        let focusedIndex = this.filteredChildren.indexOf(selected);
        if (focusedIndex < 0) {
            focusedIndex = 0;
        }
        this.setState({
            target,
            targetInput: target.querySelector('.Input'),
            open: !!this.filteredChildren,
            focusedIndex,
            selectedIndex: selected ? focusedIndex : -1
        }, cb);
        this.props.onFocus?.(event);
    }
    close(blur = true, afterCloseHandler) {
        this.setState({
            open: false
        }, ()=>{
            blur && this.blur();
            afterCloseHandler?.();
            this.props.onBlur?.();
        });
    }
    getDefaultSearchText(props) {
        return this._getLabelFromOption(this.getSelected(props.children, props.value)) || '';
    }
    getDefaultedFreetextSearchText(value) {
        return defaultToEmptyString(this._getLabelFromOption(this.getSelected(this.props.children, value)) || toStringWhenNumber(value));
    }
    reset(origin) {
        if (origin === 'esc' && this.props.freetext) return;
        const searchText = this.props.freetext ? this.props.value : this.getDefaultSearchText(this.props);
        this.filteredChildren = this.getFilteredChildren(this.props.children);
        this.setState({
            searchText
        });
    }
    getSelected(children, value) {
        let option = null;
        Children.forEach(children, (child)=>{
            if (this._getValueFromOption(child) === value) {
                option = child;
            }
        });
        return option;
    }
    getMultipleLabels(children, value) {
        return compose(map((c)=>({
                label: this._getLabelFromOption(c),
                key: this._getValueFromOption(c)
            })), filterChildren((c)=>any((val)=>val === this._getValueFromOption(c), value || [])))(children);
    }
    setFocusedIndex(index) {
        this.setState({
            focusedIndex: index
        });
    }
    changeValue = (event, newValue)=>{
        if (this.props.multiSelect) {
            this.setState({
                searchText: ''
            });
        }
        if (this.props.value === newValue) {
            this.reset();
            this.props.onNotChange?.(event, newValue);
        } else {
            this.props.onChange?.(event, newValue);
        }
    };
    filterOptions(children, searchText) {
        return !searchText ? children : filterChildren((c)=>this.props.filter(this._getLabelFromOption(c), searchText, c.props), this.getFilteredChildren(children));
    }
    incrementFocusedIndex() {
        const maxIndex = Children.count(this.filteredChildren) - 1;
        let focusedIndex = this.state.focusedIndex;
        focusedIndex++;
        if (focusedIndex <= maxIndex) {
            this.setFocusedIndex(focusedIndex);
        }
    }
    decrementFocusedIndex() {
        let focusedIndex = this.state.focusedIndex;
        focusedIndex--;
        if (focusedIndex >= 0) {
            this.setFocusedIndex(focusedIndex);
        }
    }
    _handleInputFocus = (event)=>{
        if (this.props.freetext) {
            this.filteredChildren = this.getFilteredChildren(this.props.children, this.props.value);
        }
        this.open(undefined, event);
    };
    handleInputBlur = (event)=>{
        const searchText = this.getDefaultSearchText(this.props);
        if (this.state.open && isNotEmpty(this.filteredChildren)) {
            return;
        }
        // the value in the input field is not valid (isn't in the list)
        // or wasn't selected -> reset value to the last selected value
        if (!this.props.freetext && this.state.searchText !== searchText) {
            this.setState({
                open: true,
                searchText
            }, ()=>{
                this.filteredChildren = this.getFilteredChildren(this.props.children);
            });
        } else if (this.props.freetext && isEmpty(this.filteredChildren)) {
            this.close();
        }
        if (!this.skipInputBlur) {
            this.props.onInputBlur?.(event);
        }
        this.skipInputBlur = false;
    };
    handleInputChange = (event, value)=>{
        this.filteredChildren = this.filterOptions(this.props.children, value);
        this.setState({
            searchText: value,
            focusedIndex: 0
        });
        this.props.onInputChange?.(event, value);
    };
    handleListClickOutside = ()=>{
        if (!this.props.freetext) {
            this.reset();
        }
        this.close();
    };
    handleSelect = (e, option)=>{
        this.close(true, ()=>this.changeValue(e, this.getValueForChange(option)));
    };
    handleEnter = (e)=>{
        let selected;
        const focusedIndex = this.state.focusedIndex;
        if (this.filteredChildren?.[focusedIndex]) {
            selected = this.filteredChildren[focusedIndex];
            if (selected.props.disabled) {
                return;
            }
        }
        if (this.props.freetext && !selected) {
            this.close();
            return;
        }
        if (this.props.freetext) {
            return this.close(true, ()=>this.changeValue(e, this.getValueForChange(selected)));
        }
        this.changeValue(e, this.getValueForChange(selected));
        this.close();
    };
    getValueForChange = (option)=>{
        const { value, freetext, multiSelect } = this.props;
        const newVal = freetext ? this._getLabelFromOption(option) : this._getValueFromOption(option);
        return multiSelect ? [
            ...value,
            newVal
        ] : newVal;
    };
    handlePillClear = (e, key)=>{
        if (!this.props.focusOnPillClear) {
            e.stopPropagation();
        }
        this.changeValue(e, filter((val)=>val !== key, this.props.value));
    };
    getFilteredChildren = (inputFilteredChildren, multiValue = this.props.value)=>{
        if (!this.props.multiSelect || isEmpty(multiValue)) {
            return inputFilteredChildren;
        }
        return filterChildren((c)=>!any((val)=>val === this._getValueFromOption(c), multiValue || []), inputFilteredChildren);
    };
    handleInputKeyDown = (event)=>{
        switch(keycode(event)){
            case 'enter':
                this.handleEnter(event);
                break;
            case 'down':
                event.preventDefault();
                this.incrementFocusedIndex();
                break;
            case 'up':
                event.preventDefault();
                this.decrementFocusedIndex();
                break;
            case 'tab':
                this.skipInputBlur = true;
                this.close(false, ()=>this.props.onInputBlur?.(event));
                break;
            case 'esc':
                this.reset('esc');
                this.close();
                break;
            case 'backspace':
                const { multiSelect, value } = this.props;
                if (multiSelect && !this.state.searchText && !isEmpty(value)) {
                    this.changeValue(event, value.slice(0, -1));
                }
                break;
            default:
        }
    };
    handleClickPanelSelect = ()=>{
        this.focus();
    };
    handleClickPanelClear = (e)=>{
        e.preventDefault();
        this.changeValue(e, this.props.multiSelect ? [] : '');
        this.close();
    };
    renderList(filteredChildren, style) {
        const { disabled, readOnly, selectListAutoWidth } = this.props;
        const { focusedIndex, selectedIndex, target, searchText, open } = this.state;
        return !open || disabled || readOnly || !filteredChildren.length ? null : /*#__PURE__*/ React.createElement(SelectList, {
            ref: (list)=>{
                this._list = list;
            },
            searchText: searchText,
            onSelect: this.handleSelect,
            onClickOutside: this.handleListClickOutside,
            focusedIndex: focusedIndex,
            selectedIndex: selectedIndex,
            style: {
                ...style,
                width: !selectListAutoWidth && target ? getOffsetWidth(target) : 'auto'
            }
        }, filteredChildren);
    }
    renderPanelControls() {
        const { additionalPanelControlElement, controlPanelIconOpen, controlPanelIconClose, clearable, value, disabled, readOnly } = this.props;
        const panelProps = {
            controlPanelIconOpen,
            controlPanelIconClose,
            clearable,
            isOpen: this.state.open,
            value,
            disabled,
            readOnly,
            onSelect: this.handleClickPanelSelect,
            onClear: this.handleClickPanelClear,
            additionalPanelControlElement
        };
        return /*#__PURE__*/ React.createElement(SelectPanelControls, panelProps);
    }
    render() {
        const { autoComplete, additionalInputElement, autoFocus, asyncValidationInProgress, id, labelText, labelNote, labelPosition, labelClassName, className, children, floatingLabelText, floatingLabelClassName, controlClassName, controlInputClassName, validationText, validationClassName, disabled, readOnly, readOnlyInput, placeholder, multiSelect, value, simple, sizeSmall, tabIndex, maxLength, outline, tooltip, popperModifiers, optionsTargetContainer, required, positionObservationInterval, observePositionChanges, validationDisplayType } = this.props;
        const { target, targetInput, searchText } = this.state;
        const selectInputProps = {
            autoComplete,
            additionalInputElement,
            id,
            autoFocus,
            labelText,
            labelNote,
            labelPosition,
            labelClassName,
            floatingLabelText,
            floatingLabelClassName,
            controlClassName,
            controlInputClassName,
            validationText,
            validationClassName,
            disabled,
            readOnly,
            readOnlyInput,
            placeholder,
            asyncValidationInProgress,
            multiSelect,
            simple,
            maxLength,
            sizeSmall,
            tabIndex,
            panelControls: this.renderPanelControls(),
            value: searchText,
            pills: multiSelect ? this.getMultipleLabels(children, value) : undefined,
            onPillClear: this.handlePillClear,
            ref: (el)=>{
                this.control = el;
            },
            onChange: this.handleInputChange,
            onFocus: this._handleInputFocus,
            onBlur: this.handleInputBlur,
            onKeyDown: this.handleInputKeyDown,
            outline,
            tooltip,
            required,
            validationDisplayType
        };
        const list = this.renderList(this.filteredChildren);
        return /*#__PURE__*/ React.createElement("div", {
            className: cx('Select', className),
            ref: (el)=>{
                this.rootEl = el;
            }
        }, /*#__PURE__*/ React.createElement(SelectInput, selectInputProps), target ? /*#__PURE__*/ createPortal(/*#__PURE__*/ React.createElement(Popper, {
            referenceElement: targetInput,
            modifiers: popperModifiers,
            placement: "bottom-start"
        }, ({ style, scheduleUpdate, ref, update })=>/*#__PURE__*/ React.createElement("div", {
                key: `list_${this.filteredChildren.length}`,
                className: "Select-popper",
                ref: ref,
                style: style
            }, /*#__PURE__*/ React.createElement(SelectPositionObserver, {
                observationTarget: this.state.targetInput,
                scheduleUpdate: scheduleUpdate,
                positionObserverInterval: positionObservationInterval,
                observePositionChanges: observePositionChanges,
                isOpen: this.state.open,
                update: update
            }, list))), optionsTargetContainer || target) : null);
    }
}
Select.propTypes = {
    /**
	 * Element rendered in select input after real input
	 */ additionalInputElement: PropTypes.node,
    additionalPanelControlElement: PropTypes.object,
    /**
	 * True if async validation is in progress
	 */ asyncValidationInProgress: PropTypes.bool,
    autoComplete: PropTypes.node,
    /**
	 * Set autoFocus on input element
	 */ autoFocus: PropTypes.bool,
    /**
	 * Children will be rendered within the `Select` as options.
	 */ children: PropTypes.node,
    /**
	 * Custom `className` for the root.
	 */ className: PropTypes.string,
    /**
	 * If true, select will have clear button and event
	 */ clearable: PropTypes.bool,
    /**
	 * Custom `className` for the control component.
	 */ controlClassName: PropTypes.string,
    /**
	 * Custom `className` for the control input component.
	 */ controlInputClassName: PropTypes.string,
    /**
	 * Icon which will be displayed when select is open
	 */ controlPanelIconClose: PropTypes.node,
    /**
	 * Icon which will be displayed when select is closed
	 */ controlPanelIconOpen: PropTypes.node,
    /**
	 * If true, component will be disabled
	 */ disabled: PropTypes.bool,
    /**
	 * Custom filter function. Can be value from SelectFilters
	 *
	 * @param {String} value - value of children
	 * @param {String} searchText - searched text
	 * @return {Boolean} true, if searchText is part of value
	 */ filter: PropTypes.func,
    /**
	 * To which value will be filter applied
	 */ filterBy: PropTypes.string,
    /**
	 * Custom `className` for the floating label.
	 */ floatingLabelClassName: PropTypes.string,
    /**
	 * Floating label will be displayed inside of component
	 */ floatingLabelText: PropTypes.node,
    /**
	 * Can prevent focus event (open options) on pill clear
	 */ focusOnPillClear: PropTypes.bool,
    /**
	 * Preserve input on the next render even if it does not exist in the given options
	 */ freetext: PropTypes.bool,
    /**
	 * Id of `TextField` component
	 */ id: PropTypes.string,
    /**
	 * Custom `className` for the label.
	 */ labelClassName: PropTypes.string,
    /**
	 * LabelNote for the `TextField` component
	 */ labelNote: PropTypes.node,
    /**
	 * Position of label (`left`, `right`)
	 */ labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
    /**
	 * Text of label
	 */ labelText: PropTypes.node,
    /**
	 * HTML <input> maxlength attribute
	 */ maxLength: PropTypes.string,
    /**
	 * If true, select will be multiple
	 */ multiSelect: PropTypes.bool,
    /**
	 * Flag indicating whether Popper element should periodically check root position in case of necessary rerender
	 */ observePositionChanges: PropTypes.bool,
    /**
	 * On blur callback
	 *
	 * @param {Object} event - event targeting the element
	 * @return {undefined} Nothing
	 */ onBlur: PropTypes.func,
    /**
	 * On change callback
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of select
	 * @return {undefined} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * On focus callback
	 *
	 * @param {Object} event event targeting the element
	 * @return {undefined} Nothing
	 */ onFocus: PropTypes.func,
    /**
	 * On input blur callback for freetext. Blur is not fired when option is selected from list.
	 *
	 * @param {Object} event - event targeting the element
	 * @return {undefined} Nothing
	 */ onInputBlur: PropTypes.func,
    /**
	 * On input change callback
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of select
	 * @return {undefined} Nothing
	 */ onInputChange: PropTypes.func,
    /**
	 * On not change callback. Fires when selected value and input value are same.
	 *
	 * @param {Object} event - event targeting the element
	 * @param {String} newValue - new value of select
	 * @return {undefined} Nothing
	 */ onNotChange: PropTypes.func,
    /**
	 * Element which will be used for rendering select options
	 */ optionsTargetContainer: PropTypes.element,
    /**
	 * Whether to use outlined version of Input
	 */ outline: PropTypes.bool,
    /**
	 * Text of placeholder
	 */ placeholder: PropTypes.string,
    /**
	 * Modifiers for popper
	 */ popperModifiers: PropTypes.arrayOf(PropTypes.object),
    /**
	 *  Interval value in "ms" used for checking whether target element position hasn't changed when Popper is open
	 */ positionObservationInterval: PropTypes.number,
    /**
	 * Component is read only
	 */ readOnly: PropTypes.bool,
    /**
	 * Input of select is read only
	 */ readOnlyInput: PropTypes.bool,
    /**
	 * Show whether Component is required
	 */ required: PropTypes.bool,
    /**
	 * When true, width for Select-list will be set to auto.
	 */ selectListAutoWidth: PropTypes.bool,
    /**
	 * If true, Select will be rendered using SimpleInput
	 */ simple: PropTypes.bool,
    /**
	 * If true, Select will be rendered with sizeSmall modifier
	 */ sizeSmall: PropTypes.bool,
    /**
	 * Input's tabindex.
	 */ tabIndex: PropTypes.string,
    /**
	 * Tooltip children, either string or custom element
	 */ tooltip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    /**
	 * Custom `className` for validation.
	 */ validationClassName: PropTypes.string,
    /**
	 * Where to display validation
	 */ validationDisplayType: validationDisplayTypePropType,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Value of select
	 */ value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.array,
        PropTypes.object,
        PropTypes.bool
    ]),
    /**
	 * From which props should be value taken
	 */ valueBy: PropTypes.string
};
export { Select };
export default withDefaultComponentProps(COMPONENT_NAME, {
    observePositionChanges: true,
    controlPanelIconOpen: /*#__PURE__*/ React.createElement(AngleDown, null),
    controlPanelIconClose: /*#__PURE__*/ React.createElement(AngleUp, null),
    filter: SelectFilters.caseSensitive,
    valueBy: 'value',
    filterBy: 'label',
    focusOnPillClear: true
}, Select);
